import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Field, FormikProps } from "formik";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { Loading } from "../../components/Loading";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { vehicleExpenseApi } from "./apiService";
import ExpenseForm from "./components/ExpenseForm";
import {
  displayErrorToast,
  ExpenseEntry,
  ExpenseFormData,
  mapExpenseEntryToApiRequest,
  PaymentDetail,
} from "./components/types";
import { validationSchema } from "./validationSchema";
import { useToast } from "../../components/Toast";
import { fetchDocuments } from "../../utils/FetchDocuments";
import { uploadFile } from "../../utils/UploadFile";
import { v4 as uuidv4 } from "uuid";
import GenericOtherExpenseFields from "./components/GenericOtherExpenseFields";
import { usePlanStatus } from "../../hooks/usePlanStatus";

function EditExpense() {
  const navigate = useNavigate();
  const { expenseId } = useParams<{ expenseId: string }>();
  const { user, loading: authLoading } = useFirebaseAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<ExpenseFormData>({
    vehicleNumber: "",
    vehicleId: "",
    expenses: [],
    odometerReadings: new Map<
      string,
      {
        index: number;
        odometerReading: number;
        fuelQuantity: number;
        readingDate: string;
      }
    >(),
  });
  const { planStatus } = usePlanStatus();

  useEffect(() => {
    const loadExpenseData = async () => {
      if (!expenseId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch expense details
        const expenseData = await vehicleExpenseApi.getExpense(expenseId);
        const documents = await fetchDocuments({
          documentType: "vehicle-expense",
          documentTypeId: expenseId,
          documentContext: "vehicle-expense",
        });

        // Transform API data to form format
        const details = expenseData.details || {};
        const transformedExpense: ExpenseEntry = {
          id: expenseData.id,
          type: mapApiExpenseTypeToUI(expenseData.expenseType),
          expenseDate: expenseData.expenseDate,
          amount: expenseData.amount.toString(),
          paidTo: expenseData.paidTo,
          location: expenseData.location,
          contactNumber: expenseData.contactNumber || "",
          notes: expenseData.notes || "",
          payments: expenseData.payments || [],
          remainingAmount: calculateRemainingAmount(
            expenseData.amount,
            expenseData.payments || []
          ),

          // Initialize type-specific fields with proper defaults from details object
          fuelType: details.fuelType || "",
          liters: details.liters?.toString() || "",
          pricePerLitre: details.pricePerLitre?.toString() || "",
          bulkDiscountApplied: details.bulkDiscountApplied?.toString() || "",
          petrolPumpId: details.petrolPumpId || "",
          odometerReading: details.odometerReading?.toString() || "",
          fuelCardId: details.fuelCardId || "",

          serviceType: details.serviceType || "",
          serviceProvider: details.serviceProvider || "",
          serviceProviderType: details.serviceProviderType,
          serviceProviderId: details.serviceProviderId || "",
          maintenanceItem: details.maintenanceItem || "",
          customMaintenanceItem: details.customMaintenanceItem || "",
          actionType: details.actionType,
          warrantyPeriod: details.warrantyPeriod || "",
          warrantyStartDate: details.warrantyStartDate || "",
          warrantyEndDate: details.warrantyEndDate || "",
          customWarrantyPeriod: details.customWarrantyPeriod || "",
          nextServiceDue: details.nextServiceDue?.toString() || "",
          invoiceNumber: details.invoiceNumber || "",
          workOrderNumber: details.workOrderNumber || "",
          gst: expenseData.gst || 0,

          category: details.category,
          referenceId: details.referenceId || "",
          customCategory: details.customCategory || "",

          // Challan expense fields
          responsibleParty: details.responsibleParty || "",
          responsiblePartyName: details.responsiblePartyName || "",
          challanType: details.challanType || "",
          challanNumber: details.challanNumber || "",
          challanAction: details.challanAction || "",

          uploadedFiles: documents || [],

          masterReferenceId: expenseData.masterReferenceId || "",
          masterCategory: expenseData.masterCategory || "",
          companyId: expenseData.companyId || "",
          createdBy: expenseData.createdBy || "",
          isEdited: false,
          isNew: false,
          isDeleted: false,
        };

        const currentOdometerReadings =
          await vehicleExpenseApi.getCurrentOdometerReadings(
            expenseData?.vehicle?.id || "",
            "VEHICLE_EXPENSE"
          );

        const odometerReadingsMap = new Map<
          string,
          {
            index: number;
            odometerReading: number;
            fuelQuantity: number;
            readingDate: string;
          }
        >();

        currentOdometerReadings.forEach((reading: any, index: number) => {
          const key = reading.expenseId || uuidv4();

          // Convert liters to number, default to 0 if null or invalid
          const fuelQuantity = reading.liters ? parseFloat(reading.liters) : 0;

          // Create the reading object with negative index to avoid conflicts with form entries
          const readingObj = {
            index: -(index + 1), // Use negative index for existing readings
            odometerReading: reading.odometerReading,
            fuelQuantity: fuelQuantity,
            readingDate: reading.expenseDate,
          };

          odometerReadingsMap.set(key, readingObj);
        });

        setInitialValues({
          vehicleNumber: expenseData?.vehicle?.vehicleNumber || "",
          vehicleId: expenseData?.vehicle?.id || expenseData?.vehicleId || "",
          acquisitionType: expenseData?.vehicle?.acquisitionType || "",
          expenses: [transformedExpense],
          odometerReadings: odometerReadingsMap,
        });
      } catch (err) {
        console.error("Error loading expense data:", err);
        setError("Failed to load expense data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadExpenseData();
  }, [expenseId]);

  const mapApiExpenseTypeToUI = (apiType: string): string => {
    const typeMap: Record<string, string> = {
      FUEL: "Fuel",
      REPAIR: "Maintenance / Repair",
      DRIVER_CLEANER: "Driver/Cleaner",
      PARKING: "Parking",
      OTHER: "Other",
      INSURANCE: "Insurance",
      CHALLAN: "Challan",
      OUTSOURCED_OWNER: "Outsourced Owner",
      PICKUP_MAN: "Pickup Man",
    };
    return typeMap[apiType] || "Other";
  };

  const calculateRemainingAmount = (
    totalAmount: number,
    payments: PaymentDetail[]
  ): number => {
    const paidAmount = payments
      .filter((payment) => !payment.deletedAt)
      .reduce((sum, payment) => sum + parseFloat(payment.amount.toString()), 0);
    return Math.max(0, totalAmount - paidAmount);
  };

  const handleSubmit = async (values: ExpenseFormData) => {
    if (!expenseId) {
      console.error("No expense ID available");
      return;
    }

    try {
      setSubmitting(true);
      const expense = values.expenses[0];
      const originalExpense = initialValues.expenses[0];

      const paymentsToCreate: PaymentDetail[] = [];
      const paymentsToDelete: string[] = [];

      expense.payments?.forEach((payment) => {
        // For new payments (no ID), add to create list
        if (!payment.id) {
          paymentsToCreate.push(payment);
          return;
        }
        if (payment.isDeleted) {
          paymentsToDelete.push(payment.id);
          return;
        }
        const originalPayment = initialValues.expenses[0]?.payments?.find(
          (p) => p.id === payment.id
        );
        // If payment exists and has changes
        if (originalPayment && payment.isEdited) {
          // Add original payment ID to delete list
          paymentsToDelete.push(payment.id);
          // Add modified payment to create list
          paymentsToCreate.push({
            ...payment,
            id: undefined,
          });
        }
      });

      // Update expense with all details including edited payments
      const expenseToUpdate = mapExpenseEntryToApiRequest(
        { ...expense },
        values.vehicleNumber,
        values.vehicleId
      );

      const expenseToCreateSoftDelete = mapExpenseEntryToApiRequest(
        { ...originalExpense },
        values.vehicleNumber,
        values.vehicleId
      );

      let promises: Promise<any>[] = [];
      if (expense.isEdited) {
        promises.push(
          vehicleExpenseApi.createSoftDeleteExpense(expenseToCreateSoftDelete)
        );
        promises.push(
          vehicleExpenseApi.updateExpense(expenseId, expenseToUpdate)
        );
      }
      if (paymentsToCreate.length > 0) {
        promises.push(
          vehicleExpenseApi.createPayments(expenseId, paymentsToCreate)
        );
      }
      if (paymentsToDelete.length > 0) {
        promises.push(vehicleExpenseApi.deletePayments(paymentsToDelete));
      }
      if (expense.selectedFiles && expense.selectedFiles?.length > 0) {
        const uploadPromises = expense.selectedFiles?.map((file: File) =>
          uploadFile({
            file: file,
            documentType: "vehicle-expense",
            documentTypeId: expense.id || "",
            documentContext: "vehicle-expense",
            description: "Expense proof for vehicle expense",
          })
        );
        promises.push(...uploadPromises);
      }
      await Promise.all(promises);
      showToast("Expense updated successfully", "success");
      navigate(-1);
    } catch (error) {
      console.error("Error updating expense:", error);
      setError("Failed to update expense. Please try again.");
      showToast("Failed to update expense", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading message="Loading expense details..." fullScreen={true} />;
  }

  if (error) {
    return (
      <Container sx={{ p: 1.5 }}>
        <Alert severity="error" sx={{ mb: 1.5, fontSize: "0.75rem" }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: "1rem" }} />}
          onClick={() => navigate(-1)}
          size="small"
          sx={{ fontSize: "0.75rem" }}
        >
          Back
        </Button>
      </Container>
    );
  }

  // Don't render form until we have expense data
  if (!initialValues.expenses.length || !initialValues.vehicleNumber) {
    return <Loading message="Loading expense details..." fullScreen={true} />;
  }

  return (
    <Container sx={{ p: 1, my: 3 }}>
      <Box
        sx={{
          mb: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          size="small"
          sx={{ mb: 1, fontSize: "0.75rem" }}
        >
          Back
        </Button>
        <Typography
          variant="h6"
          component="h1"
          sx={{
            fontWeight: 600,
            color: (theme: Theme) => theme.palette.primary.main,
            fontSize: "1rem",
          }}
        >
          Edit Expense
        </Typography>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={true}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps: FormikProps<ExpenseFormData>) => {
          const expense = formikProps.values.expenses[0];

          // Form is ready for editing

          return (
            <Box
              component="form"
              noValidate
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                formikProps.handleSubmit(e);
              }}
            >
              {/* Vehicle Number (Read-only) */}
              <Paper
                elevation={3}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  mb: 1.5,
                }}
              >
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Vehicle Number"
                      name="vehicleNumber"
                      value={formikProps.values.vehicleNumber}
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      size="small"
                      sx={{
                        "& .MuiInputBase-input": { fontSize: "0.75rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem", mb: 0.25 }}
                      >
                        Total Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{ fontWeight: 600, fontSize: "1rem" }}
                      >
                        ₹{Number(expense?.amount || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Expense Form */}
              {expense && expense?.type !== "Other" ? (
                <ExpenseForm
                  type={expense.type}
                  index={0}
                  typeIndex={0}
                  onDelete={() => {}} // No delete in edit mode
                  formikProps={formikProps}
                  showDelete={false} // Hide delete button to maintain change log
                />
              ) : (
                <GenericOtherExpenseFields
                  index={0}
                  masterCategory="VEHICLE_EXPENSE"
                  masterReferenceId={expense?.masterReferenceId || ""}
                  companyId={planStatus?.companyId!}
                  createdBy={planStatus?.user?.id}
                  defaultDate={new Date().toISOString().split("T")[0]}
                  isEdit={true}
                  showDelete={false}
                />
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                  size="small"
                  sx={{ fontSize: "0.75rem", px: 2, py: 0.5 }}
                >
                  Cancel
                </Button>
                <Button
                  // type="submit"
                  onClick={() => {
                    displayErrorToast(formikProps.errors, showToast);
                    formikProps.handleSubmit();
                  }}
                  variant="contained"
                  disabled={submitting || !formikProps.dirty}
                  size="small"
                  sx={{ fontSize: "0.75rem", px: 2, py: 0.5 }}
                >
                  {submitting ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      <span>Updating...</span>
                    </Box>
                  ) : (
                    "Update Expense"
                  )}
                </Button>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Container>
  );
}

export default EditExpense;
